import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, styled, Stack, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
// Firebase Imports
import { db } from '../../firebaseConfig'; 
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

// --- STYLED COMPONENTS (NO CHANGES MADE) ---

const EducationWrapper = styled(Box)({
    padding: '80px 0',
    background: '#000',
    color: '#fff',
    position: 'relative',
    overflowX: 'hidden',
});

const TimelineTrack = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '200px', 
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #3b82f6, transparent)',
    zIndex: 1,
    [theme.breakpoints.down('md')]: {
        display: 'none', 
    }
}));

const MilestoneContainer = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    justifyContent: 'space-around', 
    alignItems: 'stretch',
    position: 'relative',
    zIndex: 2,
    gap: '24px', 
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px', 
        marginTop: '20px'
    }
}));

const MilestonePoint = styled(motion.div)(({ theme }) => ({
    width: '18px',
    height: '18px',
    background: '#000',
    border: '3px solid #3b82f6',
    borderRadius: '50%',
    position: 'absolute',
    top: '191px', 
    left: '50%',
    transform: 'translateX(-50%)',
    boxShadow: '0 0 15px #3b82f6',
    zIndex: 3,
    [theme.breakpoints.down('md')]: {
        display: 'none',
    }
}));

const EduCard = styled(motion.div)(({ theme }) => ({
    width: '100%',
    maxWidth: '350px',
    background: 'rgba(15, 23, 42, 0.7)',
    backdropFilter: 'blur(10px)',
    padding: '25px',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    textAlign: 'center',
    position: 'relative',
    marginTop: '230px', 
    [theme.breakpoints.down('md')]: {
        marginTop: '0px', 
    },
    '&:hover': {
        borderColor: '#3b82f6',
    }
}));

// --- COMPONENT ---

const HorizontalEducation = () => {
    const [eduData, setEduData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch Data from Firestore
    useEffect(() => {
        const fetchEducation = async () => {
            try {
                const q = query(collection(db, "education"), orderBy("year", "desc"));
                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setEduData(data);
            } catch (error) {
                console.error("Error fetching education data: ", error);
            } finally {
                setLoading(false);
            }
        };

        fetchEducation();
    }, []);

    // Helper to render the correct icon based on iconType string
    const renderIcon = (type) => {
        switch(type) {
            case 'book': return <MenuBookIcon />;
            case 'bank': return <AccountBalanceIcon />;
            default: return <SchoolIcon />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ py: 10, textAlign: 'center', background: '#000' }}>
                <CircularProgress sx={{ color: '#3b82f6' }} />
            </Box>
        );
    }

    return (
        <EducationWrapper id="education"
        sx={{ background: 'linear-gradient(135deg, #213258 0%, #312c2c 100%)' }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography variant="h3" sx={{ fontWeight: 900 }}>
                        Education <span style={{ color: '#3b82f6' }}>Timeline</span>
                    </Typography>
                </Box>

                <Box sx={{ position: 'relative' }}>
                    <TimelineTrack />
                    
                    <MilestoneContainer>
                        {eduData.map((item, index) => (
                            <Box key={item.id || index} sx={{ 
                                position: 'relative', 
                                width: { xs: '100%', md: '30%' }, 
                                display: 'flex', 
                                justifyContent: 'center' 
                            }}>
                                <MilestonePoint 
                                    initial={{ scale: 0 }}
                                    whileInView={{ scale: 1 }}
                                    viewport={{ once: true }}
                                />

                                <EduCard
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <Box sx={{ mb: 2, color: '#3b82f6' }}>
                                        {/* Render dynamic icon from Firestore data */}
                                        {renderIcon(item.iconType)}
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 800 }}>{item.degree}</Typography>
                                    <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1 }}>{item.inst}</Typography>
                                    <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 900, display: 'block', mb: 2 }}>
                                        {item.year}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                        {item.desc}
                                    </Typography>
                                </EduCard>
                            </Box>
                        ))}
                    </MilestoneContainer>
                </Box>
            </Container>
        </EducationWrapper>
    );
};

export default HorizontalEducation;
